/**
 * WorkflowPanel — Enterprise Approval Workflow UI.
 *
 * Displays the current user's notice drafts and the available workflow actions
 * based on each draft's status and the user's role (owner = reviewer/approver,
 * member = drafter).
 *
 * Lifecycle: Draft -> Review -> Approved -> Published.
 * The existing Publish / Version History / Compare / Rollback UI lives in
 * ConsentNoticeDesigner and is NOT modified here — this panel is additive.
 */
import { useState } from 'react';
import type {
  NoticeDraftDetail,
  NoticeDraftStatus,
  NoticeDraftSummary,
  ReviewComment,
} from '../../lib/types';
import { Spinner } from '../../components/Spinner';
import { SkeletonCard, SkeletonTable } from '../../components/ui/Skeleton';
import { useToast } from '../../components/Toast';
import {
  addReviewComment,
  approveDraft,
  getDraft,
  getNetworkErrorMessage,
  publishDraft,
  rejectDraft,
  returnDraft,
  reviseDraft,
  submitDraft,
} from '../../lib/api';

interface WorkflowPanelProps {
  drafts: NoticeDraftSummary[];
  draftsLoading: boolean;
  draftsError: string | null;
  /** 'owner' = reviewer/approver, 'member' = drafter. */
  userRole: 'owner' | 'member';
  /** Called after any successful workflow transition so the parent refreshes. */
  onWorkflowChanged: () => Promise<void>;
}

const STATUS_STYLES: Record<NoticeDraftStatus, string> = {
  draft: 'border border-line bg-surface text-sub',
  review: 'border border-amber-500/20 bg-amber-500/10 text-amber-400',
  approved: 'border border-blue-100 bg-blue-50/50 text-blue',
  rejected: 'border border-err/20 bg-err/10 text-err',
  published: 'border border-emerald-500/20 bg-emerald-500/10 text-emerald-400',
};

const STATUS_LABELS: Record<NoticeDraftStatus, string> = {
  draft: 'Draft',
  review: 'In Review',
  approved: 'Approved',
  rejected: 'Rejected',
  published: 'Published',
};

export function WorkflowPanel({
  drafts,
  draftsLoading,
  draftsError,
  userRole,
  onWorkflowChanged,
}: WorkflowPanelProps) {
  const { addToast } = useToast();
  const [activeDraftId, setActiveDraftId] = useState<string | null>(null);
  const [detail, setDetail] = useState<NoticeDraftDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [commentText, setCommentText] = useState('');

  const activeDraftSummary = drafts.find((d) => d.id === activeDraftId) ?? null;
  const isOwner = userRole === 'owner';

  const openDraft = async (draft: NoticeDraftSummary) => {
    setActiveDraftId(draft.id);
    setDetail(null);
    setDetailLoading(true);
    try {
      const fetched = await getDraft(draft.id);
      setDetail(fetched);
    } catch (e) {
      addToast({ type: 'error', message: getNetworkErrorMessage(e, 'Failed to load draft.') });
      setActiveDraftId(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDraft = () => {
    setActiveDraftId(null);
    setDetail(null);
    setCommentText('');
  };

  // Wrap every workflow action: show toast + refresh on success, toast on error.
  const runAction = async (
    label: string,
    fn: () => Promise<{ status: string }>,
    { successType = 'success' as 'success' | 'warning', successMsg }: { successType?: 'success' | 'warning'; successMsg: string },
  ) => {
    setActionLoading(true);
    try {
      const result = await fn();
      addToast({ type: successType, message: successMsg });
      await onWorkflowChanged();
      // Refresh the open detail view if it's the same draft.
      if (activeDraftId) {
        const refreshed = await getDraft(activeDraftId);
        setDetail(refreshed);
      }
      return result;
    } catch (e) {
      addToast({ type: 'error', message: getNetworkErrorMessage(e, `${label} failed.`) });
      throw e;
    } finally {
      setActionLoading(false);
    }
  };

  const handleSubmit = (draft: NoticeDraftSummary) =>
    runAction('Submit', () => submitDraft(draft.id, commentText || undefined), {
      successMsg: 'Draft submitted for review.',
    }).catch(() => {});

  const handleApprove = (draft: NoticeDraftSummary) =>
    runAction('Approve', () => approveDraft(draft.id, commentText || undefined), {
      successMsg: 'Draft approved. It can now be published.',
    }).then(async () => {
      setCommentText('');
    })
    .catch(() => {});

  const handleReject = (draft: NoticeDraftSummary) => {
    if (!commentText.trim()) {
      addToast({ type: 'warning', message: 'A comment is required to reject a draft.' });
      return;
    }
    runAction('Reject', () => rejectDraft(draft.id, commentText), {
      successType: 'warning',
      successMsg: 'Draft rejected and returned to the author.',
    })
      .then(async () => {
        setCommentText('');
      })
      .catch(() => {});
  };

  const handleReturn = (draft: NoticeDraftSummary) => {
    if (!commentText.trim()) {
      addToast({ type: 'warning', message: 'A comment is required to return a draft.' });
      return;
    }
    runAction('Return', () => returnDraft(draft.id, commentText), {
      successType: 'warning',
      successMsg: 'Draft returned for revision.',
    })
      .then(async () => {
        setCommentText('');
      })
      .catch(() => {});
  };

  const handleRevise = (draft: NoticeDraftSummary) =>
    runAction('Revise', () => reviseDraft(draft.id), {
      successMsg: 'Draft is back in edit mode.',
    }).catch(() => {});

  const handlePublish = (draft: NoticeDraftSummary) =>
    runAction('Publish', () => publishDraft(draft.id), {
      successMsg: 'Draft published successfully.',
    }).catch(() => {});

  const handleAddComment = async () => {
    if (!activeDraftId || !commentText.trim()) return;
    setActionLoading(true);
    try {
      await addReviewComment(activeDraftId, commentText.trim());
      addToast({ type: 'success', message: 'Comment added.' });
      setCommentText('');
      if (activeDraftId) {
        const refreshed = await getDraft(activeDraftId);
        setDetail(refreshed);
      }
    } catch (e) {
      addToast({ type: 'error', message: getNetworkErrorMessage(e, 'Failed to add comment.') });
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div className="rounded-2xl border border-line bg-card p-6 shadow-card">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-bold text-ink flex items-center gap-2">
          <span className="h-2 w-2 rounded-full bg-blue"></span>
          Approval Workflow
        </h3>
        <span className="text-[10px] font-semibold uppercase tracking-wider text-muted">
          Your role: <span className={isOwner ? 'text-blue font-bold' : 'text-sub'}>{userRole}</span>
        </span>
      </div>

      {/* Loading */}
      {draftsLoading && (
        <div className="py-4">
          <SkeletonTable rows={3} columns={3} />
        </div>
      )}

      {/* Error */}
      {draftsError && !draftsLoading && (
        <div className="rounded-xl border border-err/20 bg-err/10 p-4 text-xs text-err">
          <p>{draftsError}</p>
        </div>
      )}

      {/* Empty */}
      {!draftsLoading && !draftsError && drafts.length === 0 && (
        <div className="flex flex-col items-center justify-center py-8 text-center">
          <div className="text-3xl text-muted mb-2">📝</div>
          <p className="text-sm text-sub">No drafts in the workflow.</p>
          <p className="text-xs text-muted mt-1">
            Create a draft to start the approval process.
          </p>
        </div>
      )}

      {/* Drafts list */}
      {!draftsLoading && !draftsError && drafts.length > 0 && (
        <div className="space-y-2">
          {drafts.map((draft) => (
            <div
              key={draft.id}
              className={`flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-xl border p-3 transition ${
                activeDraftId === draft.id
                  ? 'border-blue bg-blue-50/30'
                  : 'border-line bg-surface hover:border-line'
              }`}
            >
              <div className="flex items-center gap-3">
                <span
                  className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[10px] font-bold ${STATUS_STYLES[draft.status]}`}
                >
                  {STATUS_LABELS[draft.status]}
                </span>
                <div>
                  <p className="text-xs text-sub">
                    by <span className="font-semibold text-ink">{draft.authorEmail}</span>
                  </p>
                  {draft.version !== null && (
                    <p className="text-[10px] text-muted">Published as v{draft.version}</p>
                  )}
                  {draft.reviewerEmail && (
                    <p className="text-[10px] text-muted">
                      reviewed by {draft.reviewerEmail}
                    </p>
                  )}
                </div>
              </div>
              <button
                type="button"
                onClick={() =>
                  activeDraftId === draft.id ? closeDraft() : openDraft(draft)
                }
                className="btn-g self-start sm:self-auto px-3 py-1.5 text-[11px]"
              >
                {activeDraftId === draft.id ? 'Close' : 'View'}
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Expanded draft detail + workflow actions */}
      {activeDraftId && (
        <div className="mt-4 rounded-xl border border-line bg-surface p-4 space-y-4">
          {detailLoading && (
            <div className="py-2">
              <SkeletonCard />
            </div>
          )}

          {!detailLoading && activeDraftSummary && detail && (
            <>
              {/* Workflow action bar (context-sensitive) */}
              <WorkflowActionBar
                status={activeDraftSummary.status}
                isOwner={isOwner}
                actionLoading={actionLoading}
                onSubmit={() => handleSubmit(activeDraftSummary)}
                onApprove={() => handleApprove(activeDraftSummary)}
                onReject={() => handleReject(activeDraftSummary)}
                onReturn={() => handleReturn(activeDraftSummary)}
                onRevise={() => handleRevise(activeDraftSummary)}
                onPublish={() => handlePublish(activeDraftSummary)}
              />

              {/* Comment input */}
              <div className="space-y-2">
                <label className="block text-[10px] font-bold uppercase tracking-wider text-muted">
                  Comment{' '}
                  {(activeDraftSummary.status === 'review' && isOwner) ||
                  activeDraftSummary.status === 'rejected'
                    ? ''
                    : '(optional)'}
                  {(activeDraftSummary.status === 'review' &&
                    (isOwner
                      ? ' — required to reject / return'
                      : '')) || ''}
                </label>
                <textarea
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  rows={2}
                  placeholder="Leave a note for the reviewer or author…"
                  className="w-full rounded-lg border border-line bg-card px-3 py-2 text-xs text-ink placeholder:text-muted focus:border-blue outline-none resize-y"
                />
                <button
                  type="button"
                  onClick={handleAddComment}
                  disabled={actionLoading || !commentText.trim()}
                  className="btn-g px-3 py-1.5 text-[11px] disabled:opacity-40"
                >
                  Add Comment
                </button>
              </div>

              {/* Comments timeline */}
              {detail.comments.length > 0 && (
                <CommentTimeline comments={detail.comments} />
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Workflow action bar — shows only the buttons valid for the current status.
// ---------------------------------------------------------------------------

interface WorkflowActionBarProps {
  status: NoticeDraftStatus;
  isOwner: boolean;
  actionLoading: boolean;
  onSubmit: () => void;
  onApprove: () => void;
  onReject: () => void;
  onReturn: () => void;
  onRevise: () => void;
  onPublish: () => void;
}

function WorkflowActionBar({
  status,
  isOwner,
  actionLoading,
  onSubmit,
  onApprove,
  onReject,
  onReturn,
  onRevise,
  onPublish,
}: WorkflowActionBarProps) {
  const btn =
    'rounded-lg px-4 py-2 text-[11px] font-semibold text-white shadow disabled:opacity-50 transition flex items-center justify-center gap-1.5';
  const primary = `${btn} bg-blue hover:bg-blue/90`;
  const successBtn = `${btn} bg-emerald-600 hover:bg-emerald-500`;
  const warnBtn = `${btn} bg-amber-600 hover:bg-amber-500`;
  const dangerBtn = `${btn} bg-err hover:bg-err/90`;

  return (
    <div className="rounded-lg border border-line bg-card p-3">
      <p className="text-[10px] font-bold uppercase tracking-wider text-muted mb-2">
        Available actions
      </p>
      <div className="flex flex-wrap gap-2">
        {status === 'draft' && (
          <button type="button" onClick={onSubmit} disabled={actionLoading} className={primary}>
            {actionLoading ? <Spinner size="sm" /> : '📤'} Submit for Review
          </button>
        )}

        {status === 'review' && isOwner && (
          <>
            <button type="button" onClick={onApprove} disabled={actionLoading} className={successBtn}>
              {actionLoading ? <Spinner size="sm" /> : '✓'} Approve
            </button>
            <button type="button" onClick={onReject} disabled={actionLoading} className={dangerBtn}>
              ✕ Reject
            </button>
            <button type="button" onClick={onReturn} disabled={actionLoading} className={warnBtn}>
              ↩ Return to Draft
            </button>
          </>
        )}

        {status === 'review' && !isOwner && (
          <p className="text-xs text-muted italic py-1">
            Awaiting owner review.
          </p>
        )}

        {status === 'approved' && isOwner && (
          <button type="button" onClick={onPublish} disabled={actionLoading} className={successBtn}>
            {actionLoading ? <Spinner size="sm" /> : '🚀'} Publish
          </button>
        )}

        {status === 'approved' && !isOwner && (
          <p className="text-xs text-muted italic py-1">
            Approved — an owner can publish this.
          </p>
        )}

        {status === 'rejected' && (
          <button type="button" onClick={onRevise} disabled={actionLoading} className={primary}>
            ↻ Revise & Resubmit
          </button>
        )}

        {status === 'published' && (
          <p className="text-xs text-emerald-400 italic py-1">
            This draft has been published.
          </p>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Comment timeline — chronological.
// ---------------------------------------------------------------------------

function CommentTimeline({ comments }: { comments: ReviewComment[] }) {
  return (
    <div className="space-y-2">
      <p className="text-[10px] font-bold uppercase tracking-wider text-muted">
        Comments ({comments.length})
      </p>
      <div className="space-y-2 max-h-48 overflow-y-auto pr-1">
        {comments.map((c) => (
          <div key={c.id} className="rounded-lg border border-line bg-surface p-3">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[11px] font-semibold text-sub">{c.authorEmail}</span>
              <span
                className={`inline-flex items-center rounded-full px-1.5 py-0.5 text-[9px] font-bold ${STATUS_STYLES[c.status]}`}
              >
                {STATUS_LABELS[c.status]}
              </span>
            </div>
            <p className="text-xs text-sub leading-relaxed">{c.comment}</p>
            <p className="text-[10px] text-muted mt-1">
              {new Date(c.createdAt * 1000).toLocaleString()}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}
